import React from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { cn } from '../utils/cn';

// Compact feed of bot signals that fired but did NOT become a fill — buys blocked by the LIVE
// price-impact / liquidity / anti-rug gates. Sourced from the `bot_events` table (Realtime).
// Lets you tell "quiet market" apart from "signal fired but rejected".

const KIND_META = {
  buy_rejected: { label: 'REJECTED', color: 'text-red-400 bg-red-500/10 border-red-500/30', icon: FiIcons.FiXCircle },
  liquidity_skip: { label: 'LOW LIQ', color: 'text-amber-400 bg-amber-500/10 border-amber-500/30', icon: FiIcons.FiDroplet }
};

function timeAgo(iso) {
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function BotActivity({ events = [] }) {
  const rows = events.slice(0, 15);
  return (
    <div className="glass-panel p-4 rounded-xl space-y-3">
      <div className="flex items-center gap-2 text-slate-300 font-bold uppercase tracking-wider text-[11px]">
        <SafeIcon icon={FiIcons.FiActivity} className="text-purple-400" />
        Bot Activity — Blocked Signals
      </div>

      {rows.length === 0 ? (
        <p className="text-[11px] text-slate-500">No blocked signals recently. When the bot finds a buy it can't fill (price impact, liquidity, or anti-rug), it shows up here.</p>
      ) : (
        <div className="space-y-1.5 max-h-64 overflow-y-auto custom-scrollbar pr-1">
          {rows.map((e) => {
            const meta = KIND_META[e.kind] || { label: e.kind, color: 'text-slate-400 bg-slate-500/10 border-slate-500/30', icon: FiIcons.FiInfo };
            return (
              <div key={e.id} className="bg-[#0B0E14] border border-slate-800 rounded-lg px-3 py-2 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={cn('text-[9px] font-bold px-1.5 py-0.5 rounded border flex items-center gap-1 flex-shrink-0', meta.color)}>
                      <SafeIcon icon={meta.icon} className="text-[10px]" /> {meta.label}
                    </span>
                    <span className="text-xs font-bold text-slate-200 truncate">{e.tokenSymbol || '—'}</span>
                    {(e.strategy || e.timeframe) && (
                      <span className="text-[9px] text-slate-500 font-mono flex-shrink-0">
                        {[e.strategy, e.timeframe].filter(Boolean).join('/')}
                      </span>
                    )}
                  </div>
                  <span className="text-[9px] text-slate-500 font-mono flex-shrink-0">{timeAgo(e.createdAt)}</span>
                </div>
                {e.reason && <p className="text-[10px] text-slate-400 leading-snug">{e.reason}</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
